import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Paper,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Email, 
  Visibility, 
  VisibilityOff, 
  CheckCircle, 
  Error as ErrorIcon,
  Send as SendIcon,
  Settings as SettingsIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import api from '../../services/api';
import useAuth from "../../hooks/useAuth.js";

const useStyles = makeStyles((theme) => ({
  smtpSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  testButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  saveButton: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  enableSwitch: {
    marginBottom: theme.spacing(3),
  },
  testResult: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
  },
  restrictedAccess: {
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: '#fff3cd',
    borderRadius: 8,
    border: '1px solid #ffeaa7',
  }
}));

const SmtpSettings = () => {
  const classes = useStyles();
  const { user } = useAuth();
  
  // Estados para configurações SMTP
  const [smtpConfig, setSmtpConfig] = useState({
    smtpEnabled: false,
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: true,
    emailFromName: '',
    emailSignature: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // Verifica se é empresa 1 (proprietária)
  const isOwnerCompany = user?.companyId === 1;

  // Carrega configurações ao montar o componente
  useEffect(() => {
    if (isOwnerCompany) {
      loadSmtpSettings();
    }
  }, [isOwnerCompany]);

  const loadSmtpSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/smtp-settings');
      setSmtpConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações SMTP:', error);
      toast.error('Erro ao carregar configurações de email');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setSmtpConfig(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    setTestResult(null); // Limpa resultado do teste anterior
  };

  const handleSaveSetting = async (field, value) => {
    try {
      await api.put('/companySettings/', {
        column: field,
        data: value
      });
      
      toast.success('Configuração salva com sucesso!');
      setHasChanges(false);
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error('Erro ao salvar configuração');
    }
  };

  const handleSaveAllSettings = async () => {
    try {
      setLoading(true);
      
      // Salva todas as configurações
      const promises = Object.keys(smtpConfig).map(key => 
        handleSaveSetting(key, smtpConfig[key])
      );
      
      await Promise.all(promises);
      setHasChanges(false);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Por favor, informe um email válido para o teste');
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);
      
      const response = await api.post('/smtp-test', {
        testEmail: testEmail
      });
      
      setTestResult({
        success: response.data.success,
        message: response.data.message,
        details: response.data.details
      });
      
      if (response.data.success) {
        toast.success(`Email de teste enviado com sucesso para ${testEmail}!`);
      } else {
        toast.error('Falha no teste de conexão SMTP');
      }
    } catch (error) {
      console.error('Erro no teste SMTP:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.message || 'Erro ao testar conexão',
        details: error.response?.data?.details || {}
      });
      toast.error('Erro ao testar conexão SMTP');
    } finally {
      setTesting(false);
    }
  };

  // Se não é empresa 1, mostra mensagem de acesso restrito
  if (!isOwnerCompany) {
    return (
      <Paper className={classes.restrictedAccess}>
        <Typography variant="h6" style={{ color: '#856404', marginBottom: 16 }}>
          🔒 Acesso Restrito
        </Typography>
        <Typography variant="body2" style={{ color: '#856404' }}>
          Apenas a empresa proprietária do sistema pode configurar as definições de SMTP.
          Entre em contato com a administração para alterações nas configurações de email.
        </Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className={classes.smtpSection}>
      <Typography variant="h5" className={classes.sectionTitle}>
        <Email />
        Configurações de Email (SMTP)
      </Typography>
      
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
        Configure o servidor SMTP para envio de emails do sistema. 
        Todas as empresas utilizarão estas configurações.
      </Typography>

      <Grid container spacing={3}>
        {/* Switch para habilitar/desabilitar SMTP */}
        <Grid item xs={12}>
          <FormControlLabel
            className={classes.enableSwitch}
            control={
              <Switch
                checked={smtpConfig.smtpEnabled}
                onChange={(e) => handleConfigChange('smtpEnabled', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                  Habilitar SMTP Personalizado
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {smtpConfig.smtpEnabled ? 
                    'Sistema usará configurações SMTP personalizadas' : 
                    'Sistema não enviará emails (SMTP desabilitado)'
                  }
                </Typography>
              </Box>
            }
          />
        </Grid>

        {/* Campos de configuração SMTP (apenas se habilitado) */}
        {smtpConfig.smtpEnabled && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Servidor SMTP"
                placeholder="smtp.gmail.com"
                value={smtpConfig.smtpHost}
                onChange={(e) => handleConfigChange('smtpHost', e.target.value)}
                className={classes.formField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SettingsIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Porta SMTP"
                value={smtpConfig.smtpPort}
                onChange={(e) => handleConfigChange('smtpPort', parseInt(e.target.value))}
                className={classes.formField}
                helperText="Padrão: 587 (TLS) ou 465 (SSL)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Usuário/Email"
                type="email"
                placeholder="seu-email@gmail.com"
                value={smtpConfig.smtpUser}
                onChange={(e) => handleConfigChange('smtpUser', e.target.value)}
                className={classes.formField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                label="Senha SMTP"
                value={smtpConfig.smtpPassword}
                onChange={(e) => handleConfigChange('smtpPassword', e.target.value)}
                className={classes.formField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Senha ou token de aplicativo"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smtpConfig.smtpSecure}
                    onChange={(e) => handleConfigChange('smtpSecure', e.target.checked)}
                    color="primary"
                  />
                }
                label="Conexão Segura (TLS/SSL)"
              />
            </Grid>

            <Divider style={{ width: '100%', margin: '16px 0' }} />

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Remetente"
                placeholder="Sistema de Atendimento"
                value={smtpConfig.emailFromName}
                onChange={(e) => handleConfigChange('emailFromName', e.target.value)}
                className={classes.formField}
                helperText="Nome que aparecerá nos emails"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Assinatura dos Emails"
                placeholder="Atenciosamente,&#10;Equipe de Suporte&#10;&#10;Este é um email automático, não responda a esta mensagem."
                value={smtpConfig.emailSignature}
                onChange={(e) => handleConfigChange('emailSignature', e.target.value)}
                className={classes.formField}
                helperText="Assinatura que será adicionada automaticamente aos emails"
              />
            </Grid>
          </>
        )}

        {/* Botões de ação */}
        <Grid item xs={12}>
          {/* Campo de email para teste */}
          {smtpConfig.smtpEnabled && smtpConfig.smtpHost && (
            <Box mb={2}>
              <TextField
                fullWidth
                label="Email para Teste"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Digite um email para receber o teste"
                className={classes.formField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Informe um email válido para receber o email de teste"
                variant="outlined"
              />
            </Box>
          )}

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveAllSettings}
              disabled={!hasChanges || loading}
              className={classes.saveButton}
            >
              {loading ? <CircularProgress size={20} /> : 'Salvar Configurações'}
            </Button>

            {smtpConfig.smtpEnabled && smtpConfig.smtpHost && testEmail && testEmail.includes('@') && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={handleTestConnection}
                disabled={testing || hasChanges}
                className={classes.testButton}
              >
                {testing ? 'Enviando...' : 'Enviar Teste'}
              </Button>
            )}
          </Box>
        </Grid>

        {/* Resultado do teste */}
        {testResult && (
          <Grid item xs={12}>
            <Alert 
              severity={testResult.success ? 'success' : 'error'}
              icon={testResult.success ? <CheckCircle /> : <ErrorIcon />}
              className={classes.testResult}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                {testResult.success ? 'Teste Bem-sucedido!' : 'Falha no Teste'}
              </Typography>
              <Typography variant="body2">
                {testResult.message}
              </Typography>
              {testResult.details && (
                <Box mt={1}>
                  <Typography variant="caption" color="textSecondary">
                    Detalhes: {JSON.stringify(testResult.details, null, 2)}
                  </Typography>
                </Box>
              )}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default SmtpSettings;
